import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import { filter } from 'rxjs/operators';

const APP_SURFACE = 'admin';
const MAX_QUEUE = 60;
const FLUSH_MS = 8000;

export interface QueuedAnalyticsEvent {
    occurredAt: string;
    appSurface: string;
    eventName: string;
    screenKey?: string | null;
    elementId?: string | null;
    sessionId?: string | null;
    metadata?: Record<string, unknown> | null;
    clientVersion?: string | null;
    platform?: string | null;
    clientEventId?: string | null;
}

/**
 * Sends first-party analytics to the web API (batched). Wire Router in start().
 * Global capture records `button_click` for interactive controls app-wide; optional
 * `data-track-id` / `data-analytics-id` / stable `id` refine the elementId.
 */
@Injectable({ providedIn: 'root' })
export class ProductAnalyticsTrackingService {
    private _http = inject(HttpClient);
    private _auth = inject(AuthService);
    private _router = inject(Router);

    private _queue: QueuedAnalyticsEvent[] = [];
    private _flushTimer: ReturnType<typeof setInterval> | null = null;
    private _sessionId = this._makeSessionId();
    private _started = false;
    private _globalClickAttached = false;

    start(): void {
        if (this._started) {
            return;
        }
        this._started = true;

        this._router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => {
                const url = e.urlAfterRedirects || e.url;
                const screenKey = url.split('?')[0] || '/';
                this.trackPageView(screenKey);
            });

        this._flushTimer = setInterval(() => this.flush(), FLUSH_MS);
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => this.flush());
            this._attachGlobalButtonClickCapture();
        }
    }

    trackPageView(screenKey: string): void {
        this._enqueue({
            occurredAt: new Date().toISOString(),
            appSurface: APP_SURFACE,
            eventName: 'page_view',
            screenKey: screenKey.slice(0, 512),
            sessionId: this._sessionId,
            platform: 'web',
        });
    }

    trackButtonClick(elementId: string, screenKey?: string | null): void {
        const sk =
            screenKey ??
            (this._router.url ? this._router.url.split('?')[0] : null);
        this._enqueue({
            occurredAt: new Date().toISOString(),
            appSurface: APP_SURFACE,
            eventName: 'button_click',
            screenKey: sk ? sk.slice(0, 512) : null,
            elementId: elementId.slice(0, 256),
            sessionId: this._sessionId,
            platform: 'web',
        });
    }

    flush(): void {
        if (this._queue.length === 0) {
            return;
        }
        const token = this._auth.SessionToken;
        if (!token) {
            return;
        }
        const batch = this._queue.splice(0, this._queue.length);
        const headers = new HttpHeaders()
            .set('Authorization', 'bearer ' + token)
            .set('refreshtoken', this._auth.refreshToken)
            .set('skip-loading', 'true');

        const url = `${environment.apiUrl}/api/v0.4.0/saasProductAnalytics/ingestEvents`;
        this._http.post(url, { events: batch }, { headers }).subscribe({
            error: () => {
                /* re-queue on failure — cap */
                if (this._queue.length < MAX_QUEUE) {
                    this._queue = batch.concat(this._queue).slice(0, MAX_QUEUE);
                }
            },
        });
    }

    private _enqueue(ev: QueuedAnalyticsEvent): void {
        ev.clientEventId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        this._queue.push(ev);
        if (this._queue.length >= 25) {
            this.flush();
        }
    }

    private _makeSessionId(): string {
        try {
            return crypto.randomUUID();
        } catch {
            return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
    }

    /**
     * Capture-phase listener: one `button_click` per primary click on common
     * interactive targets (buttons, links, Material buttons, role=button, etc.).
     */
    private _attachGlobalButtonClickCapture(): void {
        if (this._globalClickAttached || typeof document === 'undefined') {
            return;
        }
        this._globalClickAttached = true;
        document.addEventListener(
            'click',
            (event: MouseEvent) => {
                if (event.button !== 0) {
                    return;
                }
                const host = this._findInteractiveClickTarget(event);
                if (!host) {
                    return;
                }
                if (this._shouldSkipAutoTrack(host)) {
                    return;
                }
                const screenKey =
                    this._router.url?.split('?')[0]?.replace(/\/+$/, '') || '/';
                const elementId = this._deriveAutoElementId(host, screenKey);
                this.trackButtonClick(elementId, screenKey);
            },
            true
        );
    }

    private _findInteractiveClickTarget(event: MouseEvent): HTMLElement | null {
        const rawPath = event.composedPath?.() ?? [];
        const path =
            rawPath.length > 0
                ? rawPath
                : this._ancestorElements(event.target as Node | null);

        for (const n of path) {
            if (!(n instanceof HTMLElement)) {
                continue;
            }
            if (n.hasAttribute('data-analytics-ignore')) {
                return null;
            }
            if (this._isInteractiveClickHost(n)) {
                return n;
            }
        }
        return null;
    }

    private _ancestorElements(start: Node | null): HTMLElement[] {
        const out: HTMLElement[] = [];
        let cur: Node | null = start;
        while (cur) {
            if (cur instanceof HTMLElement) {
                out.push(cur);
            }
            cur = cur.parentNode;
        }
        return out;
    }

    private _isInteractiveClickHost(el: HTMLElement): boolean {
        const tag = el.tagName;
        if (tag === 'BUTTON') {
            return (
                !el.hasAttribute('disabled') &&
                el.getAttribute('aria-disabled') !== 'true'
            );
        }
        if (tag === 'A') {
            return (
                el.hasAttribute('href') ||
                el.hasAttribute('routerlink') ||
                el.hasAttribute('ng-reflect-router-link')
            );
        }
        if (tag === 'INPUT') {
            const t = (el as HTMLInputElement).type?.toLowerCase() ?? '';
            return t === 'button' || t === 'submit' || t === 'reset';
        }
        if (el.getAttribute('role') === 'button') {
            return el.getAttribute('aria-disabled') !== 'true';
        }
        if (
            el.classList.contains('mat-mdc-button-base') ||
            el.classList.contains('mat-mdc-icon-button')
        ) {
            return (
                !el.hasAttribute('disabled') &&
                el.getAttribute('aria-disabled') !== 'true'
            );
        }
        return false;
    }

    /** Handled explicitly by `appTrackProductClick` (bubble) to avoid duplicate rows. */
    private _shouldSkipAutoTrack(el: HTMLElement): boolean {
        let cur: HTMLElement | null = el;
        while (cur) {
            if (cur.hasAttribute('data-analytics-ignore')) {
                return true;
            }
            if (
                cur.hasAttribute('apptrackproductclick') ||
                cur.hasAttribute('appTrackProductClick') ||
                cur.hasAttribute('ng-reflect-app-track-product-click')
            ) {
                return true;
            }
            cur = cur.parentElement;
        }
        return false;
    }

    private _deriveAutoElementId(host: HTMLElement, screenKey: string): string {
        const explicit =
            host.getAttribute('data-track-id')?.trim() ||
            host.getAttribute('data-analytics-id')?.trim();
        if (explicit) {
            return explicit.slice(0, 256);
        }
        const idAttr = host.id?.trim();
        if (
            idAttr &&
            !idAttr.startsWith('mat-') &&
            !idAttr.startsWith('cdk-') &&
            !idAttr.startsWith('ng-')
        ) {
            return idAttr.slice(0, 256);
        }

        const tag = host.tagName.toLowerCase();
        const label =
            host.getAttribute('aria-label')?.trim() ||
            host.getAttribute('title')?.trim() ||
            host.textContent?.trim().replace(/\s+/g, ' ').slice(0, 80) ||
            '';
        const routeSlug =
            screenKey.replace(/^\/+|\/+$/g, '').replace(/\//g, '_') || 'root';
        const h = this._shortHash(`${tag}|${label}`);
        return `auto__${routeSlug}__${tag}__${h}`.slice(0, 256);
    }

    private _shortHash(s: string): string {
        let h = 0;
        for (let i = 0; i < s.length; i++) {
            h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
        }
        return (h >>> 0).toString(16).padStart(8, '0');
    }
}
